import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Plus, QrCode as QRIcon, Download, Loader2, Trash2, Copy, Printer } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { qrCampaigns } from "@/lib/api";
import { formatDate, formatIDR, platformLabel } from "@/lib/format";

const DEFAULT_MESSAGE = "Terima kasih sudah belanja! Scan QR ini untuk klaim cashback pembelian berikutnya dan bergabung menjadi member resmi brand kami.";

export default function QRLoyalty() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    campaign_name: "",
    reward_type: "cashback",
    reward_value: 10000,
    source_platform: "shopee",
    product_name: "",
    expiry_date: "",
    card_message: DEFAULT_MESSAGE,
  });

  const fetchList = async () => {
    setLoading(true);
    try { setList(await qrCampaigns.list()); } catch (err) { console.warn("QR campaigns load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, []);

  const create = async () => {
    if (!form.campaign_name || !form.reward_value) { toast.error("Nama campaign & reward wajib"); return; }
    try {
      const created = await qrCampaigns.create(form);
      toast.success("Campaign QR dibuat!");
      setOpen(false);
      setForm({ ...form, campaign_name: "", product_name: "" });
      fetchList();
      setSelected(created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus campaign?")) return;
    await qrCampaigns.remove(id);
    if (selected?.id === id) setSelected(null);
    toast.success("Campaign dihapus");
    fetchList();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">QR Loyalty Generator</h1>
          <p className="text-sm text-gray-500 mt-1">Buat kartu QR untuk dimasukkan ke paket marketplace. Setiap scan → database.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-qr-btn">
              <Plus className="mr-2 h-4 w-4" /> Buat QR Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Buat QR Loyalty Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nama Campaign</Label>
                <Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} placeholder="Contoh: Cashback Shopee Nov 2026" data-testid="qr-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipe Reward</Label>
                  <Select value={form.reward_type} onValueChange={(v) => setForm({ ...form, reward_type: v })}>
                    <SelectTrigger data-testid="qr-reward-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cashback">Cashback</SelectItem>
                      <SelectItem value="voucher">Voucher</SelectItem>
                      <SelectItem value="points">Poin</SelectItem>
                      <SelectItem value="free_gift">Free Gift</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nilai Reward</Label>
                  <Input type="number" value={form.reward_value} onChange={(e) => setForm({ ...form, reward_value: Number(e.target.value) })} data-testid="qr-reward-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Marketplace Asal</Label>
                  <Select value={form.source_platform} onValueChange={(v) => setForm({ ...form, source_platform: v })}>
                    <SelectTrigger data-testid="qr-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shopee">Shopee</SelectItem>
                      <SelectItem value="tiktok">TikTok Shop</SelectItem>
                      <SelectItem value="tokopedia">Tokopedia</SelectItem>
                      <SelectItem value="lazada">Lazada</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Berlaku sampai</Label>
                  <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="qr-expiry" />
                </div>
              </div>
              <div>
                <Label>Produk Terkait (opsional)</Label>
                <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Contoh: Serum Brightening 30ml" data-testid="qr-product" />
              </div>
              <div>
                <Label>Pesan Kartu Ucapan</Label>
                <Textarea value={form.card_message} onChange={(e) => setForm({ ...form, card_message: e.target.value })} rows={3} data-testid="qr-message" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-qr-btn">Buat Campaign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-gray-200">
          <CardContent className="p-0">
            {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
              : list.length === 0 ? (
                <div className="p-12 text-center">
                  <QRIcon className="mx-auto text-gray-300" size={56} />
                  <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada campaign QR</h3>
                  <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">Buat campaign pertama untuk mulai mengumpulkan database pelanggan dari marketplace.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {list.map((c) => (
                    <div key={c.id} className={`p-4 cursor-pointer hover:bg-gray-50 ${selected?.id === c.id ? "bg-emerald-50/50" : ""}`}
                      onClick={() => setSelected(c)} data-testid={`campaign-${c.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-gray-900 truncate">{c.campaign_name}</div>
                            <Badge className="bg-blue-50 text-blue-700 border-blue-200 border capitalize text-[10px]">{platformLabel(c.source_platform)}</Badge>
                            {!c.active && <Badge className="bg-gray-100 text-gray-600 border text-[10px]">Non-aktif</Badge>}
                          </div>
                          <div className="mt-1 text-sm text-gray-600 capitalize">
                            {c.reward_type} {c.reward_type !== "free_gift" && `· ${formatIDR(c.reward_value)}`}
                            {c.product_name && ` · ${c.product_name}`}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            Scan: <b>{c.scan_count || 0}</b> · Opt-in: <b>{c.optin_count || 0}</b> · Berlaku: {c.expiry_date || "-"}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(c.id); }} data-testid={`delete-qr-${c.id}`}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>

        <QRPreview campaign={selected} />
      </div>
    </div>
  );
}

const QRPreview = ({ campaign }) => {
  const canvasRef = useRef(null);
  const optinUrl = campaign ? `${window.location.origin}/qr/${campaign.id}` : "";

  const downloadPNG = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-${campaign.campaign_name.replace(/\s+/g, "-")}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(optinUrl);
    toast.success("Link disalin");
  };

  const printCard = () => {
    const win = window.open("", "_blank", "width=500,height=700");
    if (!win) return;
    const canvas = canvasRef.current?.querySelector("canvas");
    const qrData = canvas ? canvas.toDataURL("image/png") : "";

    const rewardLabel =
      campaign.reward_type === "cashback" ? `Cashback ${formatIDR(campaign.reward_value)}` :
      campaign.reward_type === "voucher" ? `Voucher ${formatIDR(campaign.reward_value)}` :
      campaign.reward_type === "points" ? `${campaign.reward_value} Poin` : "Free Gift";

    const doc = win.document;
    // Build DOM safely with createElement/textContent (no HTML injection).
    doc.title = "QR Card";

    const style = doc.createElement("style");
    style.textContent = `
      body{font-family:sans-serif;padding:24px;text-align:center;background:#fff}
      .card{border:2px dashed #059669;border-radius:16px;padding:24px;max-width:340px;margin:auto}
      .brand{color:#059669;font-weight:800;font-size:20px;margin-bottom:8px}
      .reward{background:linear-gradient(135deg,#d1fae5,#fef3c7);padding:12px;border-radius:8px;margin:12px 0;font-weight:700}
      img{width:200px;height:200px;margin:12px 0}
      .msg{font-size:13px;color:#4b5563;line-height:1.5;margin-top:12px}
      .foot{font-size:11px;color:#9ca3af;margin-top:16px}
    `;
    doc.head.appendChild(style);

    const card = doc.createElement("div");
    card.className = "card";

    const brand = doc.createElement("div");
    brand.className = "brand";
    brand.textContent = "🌱 Terima kasih!";
    card.appendChild(brand);

    const reward = doc.createElement("div");
    reward.className = "reward";
    reward.textContent = rewardLabel;
    card.appendChild(reward);

    if (qrData) {
      const img = doc.createElement("img");
      img.src = qrData;
      img.alt = "QR Code";
      card.appendChild(img);
    }

    const msg = doc.createElement("div");
    msg.className = "msg";
    msg.textContent = campaign.card_message || DEFAULT_MESSAGE;
    card.appendChild(msg);

    const foot = doc.createElement("div");
    foot.className = "foot";
    const footTop = doc.createElement("span");
    footTop.textContent = "Scan atau kunjungi:";
    foot.appendChild(footTop);
    foot.appendChild(doc.createElement("br"));
    const linkNode = doc.createElement("span");
    linkNode.textContent = optinUrl;
    foot.appendChild(linkNode);
    card.appendChild(foot);

    doc.body.appendChild(card);
    setTimeout(() => win.print(), 500);
  };

  if (!campaign) {
    return (
      <Card className="border-gray-200 border-dashed">
        <CardContent className="p-8 text-center flex flex-col items-center justify-center min-h-[380px]">
          <QRIcon className="text-gray-300" size={56} />
          <h3 className="mt-4 font-heading font-semibold text-gray-700">Preview QR Card</h3>
          <p className="mt-1 text-sm text-gray-500">Pilih campaign untuk melihat preview kartu.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-200">
      <CardContent className="p-6">
        <div className="text-xs font-bold tracking-widest uppercase text-emerald-600 mb-3">Preview Kartu</div>
        <div className="border-2 border-dashed border-emerald-300 rounded-xl p-5 text-center bg-gradient-to-br from-emerald-50/40 to-amber-50/40">
          <div className="font-heading font-bold text-emerald-700 text-lg">🌱 Terima kasih!</div>
          <div className="mt-3 bg-gradient-to-r from-emerald-100 to-amber-100 rounded-lg py-2 px-3 text-sm font-bold text-gray-900">
            {campaign.reward_type === "cashback" ? `Cashback ${formatIDR(campaign.reward_value)}` :
              campaign.reward_type === "voucher" ? `Voucher ${formatIDR(campaign.reward_value)}` :
              campaign.reward_type === "points" ? `${campaign.reward_value} Poin` : "Free Gift"}
          </div>
          <div ref={canvasRef} className="flex justify-center my-4">
            <div className="bg-white p-2 rounded">
              <QRCodeCanvas value={optinUrl} size={160} level="M" />
            </div>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed px-2">{campaign.card_message || DEFAULT_MESSAGE}</p>
          <div className="mt-3 text-[10px] text-gray-400 break-all">{optinUrl}</div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <Button size="sm" variant="outline" onClick={downloadPNG} data-testid="download-qr"><Download size={14} className="mr-1" /> PNG</Button>
          <Button size="sm" variant="outline" onClick={printCard} data-testid="print-qr"><Printer size={14} className="mr-1" /> Cetak</Button>
          <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-link"><Copy size={14} className="mr-1" /> Link</Button>
        </div>
      </CardContent>
    </Card>
  );
};
